import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export type ExcelAlign = "left" | "center" | "right";

export type ExcelColumn<T> = {
    header: string;
    field?: keyof T;
    getValue?: (row: T) => unknown;
    width?: number;
    align?: ExcelAlign;
    type?: "text" | "number" | "date";
};

const defaultDateFormatter = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
        d.getHours()
    )}:${pad(d.getMinutes())}`;
};

const defaultPrintedAtFormatter = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
        d.getHours()
    )}:${pad(d.getMinutes())}`;
};

export async function exportItemsToExcel<T extends Record<string, any>>(
    items: readonly T[],
    columns: readonly ExcelColumn<T>[],
    opts?: {
        sheetName?: string;
        fileName?: string;
        title?: string;
        dateFormatter?: (iso: string) => string;
        logoBase64?: string;
        logoExt?: "png" | "jpeg";
        userName?: string;
        printedAt?: Date;
        printedAtFormatter?: (d: Date) => string;
    }
) {
    const {
        sheetName = "Data",
        fileName = "export.xlsx",
        title,
        dateFormatter = defaultDateFormatter,
    } = opts ?? {};

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(sheetName);

    ws.columns = columns.map((c, idx) => ({
        key: c.field ? String(c.field) : `col_${idx}`,
        width: c.width ?? 18,
        style: {
            alignment: { horizontal: c.align ?? "left", vertical: "middle" },
        },
    }));

    if (title) {
        const hasLogo = !!opts?.logoBase64;

        ws.getRow(1).height = 28;

        const lastCol = columns.length;

        const metaStartCol = Math.max(1, lastCol - 2);
        const metaEndCol = lastCol;

        const metaText = (() => {
            const user = opts?.userName?.trim();
            const when = (opts?.printedAtFormatter ?? defaultPrintedAtFormatter)(
                opts?.printedAt ?? new Date()
            );
            return user ? `${user}, ${when}` : when;
        })();

        const metaCell = ws.getCell(1, metaStartCol);
        metaCell.value = metaText;
        metaCell.font = { size: 10 };
        metaCell.alignment = { vertical: "middle", horizontal: "right" };

        if (metaStartCol < metaEndCol) {
            ws.mergeCells(1, metaStartCol, 1, metaEndCol);
        }

        if (hasLogo) {
            const imageId = wb.addImage({
                base64: opts!.logoBase64!,
                extension: opts?.logoExt ?? "png",
            });

            ws.addImage(imageId, {
                tl: { col: 0, row: 0 },
                ext: { width: 70, height: 37 },
            });

            ws.getCell(1, 2).value = title; // B1
            ws.getCell(1, 2).font = { size: 14, bold: true };
            ws.getCell(1, 2).alignment = { vertical: "middle", horizontal: "left" };

            const mergeEndCol = Math.max(2, metaStartCol - 1);
            ws.mergeCells(1, 2, 1, mergeEndCol);
        } else {
            ws.getCell(1, 1).value = title;
            ws.getCell(1, 1).font = { size: 14, bold: true };
            ws.getCell(1, 1).alignment = { vertical: "middle", horizontal: "left" };

            const mergeEndCol = Math.max(1, metaStartCol - 1);
            ws.mergeCells(1, 1, 1, mergeEndCol);
        }
    }

    const headerRowIndex = title ? 2 : 1;
    const headerRow = ws.insertRow(headerRowIndex, columns.map((c) => c.header));

    headerRow.font = { bold: true };
    headerRow.height = 18;
    headerRow.alignment = { vertical: "middle" };

    headerRow.eachCell((cell) => {
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFEAEAEA" },
        };
        cell.border = {
            top: { style: "thin", color: { argb: "FF999999" } },
            left: { style: "thin", color: { argb: "FF999999" } },
            bottom: { style: "thin", color: { argb: "FF999999" } },
            right: { style: "thin", color: { argb: "FF999999" } },
        };
    });

    ws.views = [{ state: "frozen", ySplit: headerRowIndex }];

    items.forEach((item, idx) => {
        const rowValues = columns.map((col) => {
            const raw =
                col.getValue?.(item) ?? (col.field ? (item as any)[col.field] : "");

            if (raw == null) return "";

            if (col.type === "number") {
                const n = typeof raw === "number" ? raw : Number(raw);
                return Number.isFinite(n) ? n : String(raw);
            }

            if (col.type === "date") {
                return typeof raw === "string" ? dateFormatter(raw) : String(raw);
            }

            return String(raw);
        });

        const row = ws.addRow(rowValues);
        row.alignment = { vertical: "middle" };

        if (idx % 2 === 1) {
            row.eachCell((cell) => {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFF7F7F7" },
                };
            });
        }

        row.eachCell((cell) => {
            cell.border = {
                top: { style: "thin", color: { argb: "FFDDDDDD" } },
                left: { style: "thin", color: { argb: "FFDDDDDD" } },
                bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
                right: { style: "thin", color: { argb: "FFDDDDDD" } },
            };
        });
    });

    ws.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: headerRowIndex, column: columns.length },
    };

    const buf = await wb.xlsx.writeBuffer();
    saveAs(
        new Blob([buf], {
            type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName
    );
}